import { Button, FieldLabel, styled } from "@monolith-forensics/monolith-ui";
import { useRef, useState } from "react";
import { createPortal } from "react-dom";
import { usePermissions } from "../../hooks/usePermissions";
import { useSnackbar } from "notistack";
import { bufferToBase64 } from "../../utils/helpers";
import { PenLine, XIcon } from "lucide-react";
import { SignatureOverlay } from "./SignatureOverlay";
import moment from "moment";

export type SignatureFieldRef = {
  clear: () => void;
  isEmpty: () => boolean;
  getSignature: () => string | null;
  isESignature: boolean;
};

const StyledContainer = styled.div`
  .sig-img-container {
    width: 100%;
    height: 100%;
    border: 1px solid ${(props) => props.theme.palette.divider};
  }
  .sig-img {
    filter: ${(props) => (props.theme.name === "DARK" ? "invert(1)" : "unset")};
    padding: 15px;
    max-width: 100%;
    max-height: 100%;
  }
`;

export type SignatureFieldProps = {
  label?: string;
  description?: string;
  required?: boolean;
  sigFieldRef?: React.MutableRefObject<SignatureFieldRef | undefined>;
  style?: React.CSSProperties;
  useESignature?: boolean;
  onChange?: (sig: Signature | null) => void;
  signer?: string;
  defaultValue?: string;
  value?: string;
  error?: string;
  disabled?: boolean;
};

export type Signature = {
  signature: string;
  signer?: string;
  timestamp: string;
  userAgent: string;
  isESignature?: boolean;
  esig?: boolean;
  ip_address?: string;
};

const StyledButton = styled.button`
  font-size: 12px;
  padding: 20px;
  outline: none;
  border: 1px solid ${(props) => props.theme.palette.divider};
  border-radius: 5px;
  background-color: transparent;
  width: 100%;
  color: ${(props) => props.theme.palette.text.secondary};
  &:hover {
    background-color: ${(props) => props.theme.palette.action.hover};
    cursor: pointer;
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    pointer-events: none;
  }
`;

const ButtonMenu = styled.div`
  display: flex;
  flex-direction: row;
  gap: 10px;
  justify-content: space-between;
`;

export const SignatureField: React.FC<SignatureFieldProps> = ({
  label,
  description,
  required = false,
  sigFieldRef,
  style = {},
  useESignature = false,
  onChange = () => {},
  defaultValue,
  value,
  error,
  disabled = false,
  signer,
}) => {
  const isControlled = useRef(value !== undefined);

  const [valueState, setValueState] = useState<string | null | undefined>(
    defaultValue
  );

  const _value = isControlled.current ? value : valueState;

  const [open, setOpen] = useState(false);
  const [isESignature, setIsESignature] = useState(false);
  const { currentUser } = usePermissions();
  const { enqueueSnackbar } = useSnackbar();

  const handleSave = ({ signature, ...rest }: Signature) => {
    setValueState(signature || null);
    setIsESignature(false);
    onChange({ signature, ...rest, isESignature, signer });
  };

  const handleESignature = () => {
    if (currentUser?.signature) {
      const signature = currentUser.signature.data;
      setIsESignature(true);
      setValueState(bufferToBase64(currentUser.signature.data));
      enqueueSnackbar("E-signature applied", {
        variant: "success",
      });
      onChange({
        signature,
        isESignature: true,
        esig: true,
        timestamp: moment().toISOString(),
        userAgent: window.navigator.userAgent,
        signer,
      });
    } else {
      enqueueSnackbar("No saved signature found", {
        variant: "warning",
      });
    }
  };

  const handleClear = () => {
    setValueState(null);
    onChange(null);
  };

  if (sigFieldRef) {
    sigFieldRef.current = {
      clear: () => {
        setValueState(null);
      },
      isEmpty: () => {
        return !_value;
      },
      getSignature: () => {
        return _value || null;
      },
      isESignature: isESignature,
    };
  }

  return (
    <>
      <StyledContainer
        style={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          margin: "0px",
          ...style,
        }}
      >
        {label && (
          <FieldLabel size="sm" description={description} asterisk={required}>
            {label}
          </FieldLabel>
        )}
        {_value && (
          <div className="sig-img-container">
            <img className="sig-img" src={_value} alt="signature" />
          </div>
        )}
        {!_value && (
          <StyledButton
            onClick={() => setOpen(!open)}
            disabled={disabled}
            data-testid="profile-get-signature-button"
          >
            Get Signature
          </StyledButton>
        )}
        <ButtonMenu>
          {useESignature && (
            <Button
              size="xxs"
              variant="text"
              leftSection={<PenLine size={14} />}
              color="primary"
              onClick={handleESignature}
              disabled={disabled}
            >
              eSign
            </Button>
          )}
          {_value && (
            <Button
              size="xxs"
              variant="text"
              leftSection={<XIcon size={14} />}
              color="error"
              onClick={handleClear}
              disabled={disabled}
            >
              Clear
            </Button>
          )}
        </ButtonMenu>

        {open &&
          createPortal(
            <SignatureOverlay
              open={open}
              handleClose={() => setOpen(false)}
              onSave={handleSave}
              label={label || ""}
            />,
            document.body
          )}
      </StyledContainer>
    </>
  );
};
